import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useParams, useHistory, Link } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';

import { openModal } from 'reducers/modalReducer';
import { MODAL_TYPES } from 'view/ModalManager';

import { usePageTitle } from 'utils/pageTitle';
import DashboardLayout from 'pages/_rd_dashboard/DashboardLayout';
import PendingInvitationCard from 'components/PendingInvitationCard';
import { UserAvatarGroup } from 'view/components/UserAvatar';
import PortfolioPermission from 'view/components/PortfolioPermission';
import PortfolioMessages from './components/PortfolioMessages';
import PaginatorV3 from 'components/Paginator/PaginatorV3';
import { ReactComponent as SearchIcon } from 'assets/icons/search.svg';

import {
	fetchPortfolioById,
	clearSelectedPortfolio,
	clearPortfolioErrors,
	removePropertyFromPortfolioSet,
} from 'data/reducers/portfoliosManagementReducer';
import {
	usePortfolioProperties,
	PORTFOLIO_PROPERTIES_PER_PAGE,
} from 'data/repositories/portfolioPropertiesRepository';
import { revokeInvitation } from 'data/repositories/invitationsRepository';
import { showToast } from 'data/reducers/toastReducer';
import RecentAlertsSection from './components/RecentAlertsSection';

import './PortfolioDetailPage.css';
import { API_URL } from 'data/services/apiConstants';
import { SubscriptionHelper } from 'view/helpers';
import { useOrganizationContext } from 'src/contexts/OrganizationContext';

// The portfolio Messages section is hidden for now; set to true to bring it back.
// While false the section doesn't mount, so it also doesn't fetch messages.
const SHOW_PORTFOLIO_MESSAGES = false;

// How long typing in the property search pauses before the list is searched on the server.
export const PROPERTY_SEARCH_DEBOUNCE_MS = 300;

const PortfolioDetailPage = () => {
	const { portfolioId } = useParams();
	const history = useHistory();
	const dispatch = useDispatch();

	const [propertySearchQuery, setPropertySearchQuery] = useState('');
	// The page and the (debounced) search term the property list was asked for
	const [propertyListQuery, setPropertyListQuery] = useState({ page: 1, search: '' });
	// The portfolio whose page the list last received, so another portfolio's rows never show
	const [loadedPortfolioId, setLoadedPortfolioId] = useState(null);

	// Get portfolio data from Redux store
	const {
		selectedPortfolio: portfolio,
		isLoadingSelected: isLoadingPortfolio,
		selectedError: portfolioError,
	} = useSelector((state) => state.portfoliosManagement);

	// The property list: one lean, server-searched page at a time (v2 listing)
	const {
		portfolioProperties,
		portfolioPropertiesPagination,
		isLoadingPortfolioProperties,
		portfolioPropertiesError,
		actions: propertyListActions,
	} = usePortfolioProperties();
	// Revoking a pending invitation calls DELETE
	// /organizations/{organizationId}/invitations/{invitationId}, which needs
	// the caller's organization id -- something the portfolio-set response
	// does not carry. OrganizationProvider (src/contexts/OrganizationContext,
	// mounted app-wide in src/index.js) already resolves this on login and
	// keeps it current; source it from there rather than re-fetching it
	// page-locally, which would reintroduce the load race the provider
	// already closes.
	const { activeOrganization } = useOrganizationContext();

	// Fetch portfolio data on component mount
	useEffect(() => {
		if (portfolioId) {
			dispatch(clearSelectedPortfolio());
			dispatch(clearPortfolioErrors());
			dispatch(fetchPortfolioById(portfolioId));
			// Another portfolio starts from its first page, unsearched
			setPropertySearchQuery('');
			setPropertyListQuery((query) => (query.page === 1 && query.search === '' ? query : { page: 1, search: '' }));
		}

		return () => {
			dispatch(clearSelectedPortfolio());
		};
	}, [portfolioId, dispatch]);

	// Fetch the page the list is asked for
	const loadPropertyPage = useCallback(() => {
		if (!portfolioId) return;
		const result = propertyListActions.getPortfolioProperties({
			portfolioId,
			page: propertyListQuery.page,
			perPage: PORTFOLIO_PROPERTIES_PER_PAGE,
			search: propertyListQuery.search,
		});
		Promise.resolve(result)
			.then(() => setLoadedPortfolioId(portfolioId))
			.catch(() => setLoadedPortfolioId(portfolioId));
	}, [portfolioId, propertyListActions, propertyListQuery]);

	useEffect(() => {
		loadPropertyPage();
	}, [loadPropertyPage]);

	// Search on the server once typing pauses; a new term starts at page 1
	useEffect(() => {
		const term = propertySearchQuery.trim();
		const timer = setTimeout(() => {
			setPropertyListQuery((query) => (query.search === term ? query : { page: 1, search: term }));
		}, PROPERTY_SEARCH_DEBOUNCE_MS);
		return () => clearTimeout(timer);
	}, [propertySearchQuery]);

	// Adding or removing a property changes the portfolio's count: reload the page then
	const lastPropertyCount = useRef(undefined);
	useEffect(() => {
		const count = portfolio?.propertyCount;
		if (lastPropertyCount.current !== undefined && count !== undefined && count !== lastPropertyCount.current) {
			loadPropertyPage();
		}
		lastPropertyCount.current = count;
		// Only a change of the count reloads; a new page or term reloads through loadPropertyPage itself
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [portfolio?.propertyCount]);

	// Removing the last property on the last page reloads a page past the new end, which comes back
	// empty with the real total: step back to the new last page instead of showing no properties
	const lastPropertyPage = portfolioPropertiesPagination?.lastPage;
	useEffect(() => {
		const pageIsPastTheEnd =
			loadedPortfolioId === portfolioId &&
			!isLoadingPortfolioProperties &&
			Array.isArray(portfolioProperties) &&
			portfolioProperties.length === 0 &&
			lastPropertyPage >= 1 &&
			propertyListQuery.page > lastPropertyPage;
		if (pageIsPastTheEnd) {
			setPropertyListQuery((query) => ({ ...query, page: lastPropertyPage }));
		}
	}, [loadedPortfolioId, portfolioId, isLoadingPortfolioProperties, portfolioProperties, lastPropertyPage, propertyListQuery.page]);

	// Handle pagination page change
	const handlePageChange = (page) => {
		setPropertyListQuery((query) => ({ ...query, page }));
	};

	// Use real portfolio data or fallback
	const portfolioData = portfolio || {
		id: portfolioId,
		name: 'Loading...',
		description: 'Portfolio Details',
		propertyCount: 0,
		openViolationsCount: 0,
		certificatesCount: 0,
		uploadsCount: 0,
		users: [],
		pendingInvitations: [],
	};

	// Get portfolio users
	const portfolioUsers = portfolioData.users || [];

	// Invitations that have not yet been accepted. These are NOT users --
	// there is no user id yet, no name, and nothing about them belongs in
	// `portfolioUsers` -- so they are kept as their own list end-to-end and
	// rendered separately below. The API only populates this array for a
	// set owner or admin; a viewer legitimately receives [].
	const pendingInvitations = portfolioData.pendingInvitations || [];

	// Check if user is viewer
	const isViewer =
		portfolio && !portfolio.isOwned && portfolio.role === 'viewer';

	const isEnterpriseOwner = useMemo(
		() =>
			SubscriptionHelper.isEnterprisePlan(portfolio?.plan) &&
			portfolio?.isOwned,
		[portfolio],
	);

	// Set page title
	usePageTitle(`${portfolioData.name} - Portfolio Details`);

	const handleBackToPortfolios = () => {
		history.push('/dashboard/portfolio');
	};

	const handleAddUser = () => {
		dispatch(
			openModal(MODAL_TYPES.ADD_PORTFOLIO_USER_MODAL, {
				portfolioId: portfolioData.id,
			}),
		);
	};

	const handleAddProperty = () => {
		dispatch(
			openModal(MODAL_TYPES.ADD_PORTFOLIO_PROPERTY_MODAL, {
				portfolioSetId: portfolioData.id,
				users: portfolioUsers,
			}),
		);
	};

	const handleDeleteProperty = (property) => {
		const propId = property.id || property.property_id;
		if (isEnterpriseOwner) {
			handleDeleteEnterpriseOwnerProperty(property);
			return;
		}
		dispatch(
			openModal(MODAL_TYPES.CONFIRM, {
				color: 'danger',
				title: 'Remove Property',
				onConfirm: async () => {
					try {
						await dispatch(
							removePropertyFromPortfolioSet(portfolioData.id, propId),
						);
						// The new count reloads the list page (see lastPropertyCount)
						dispatch(fetchPortfolioById(portfolioData.id));
					} catch (error) {
						// removePropertyFromPortfolioSet records the error in Redux; nothing more to do here
						console.error('Removing the property from the portfolio failed:', error);
					}
				},
				children: (
					<div>
						Are you sure you want to remove &quot;{property.address}&quot; from
						this portfolio? This action cannot be undone.
					</div>
				),
				confirmLabel: 'Remove Property',
			}),
		);
	};

	const handleDeleteEnterpriseOwnerProperty = () => {
		dispatch(
			openModal(MODAL_TYPES.CONFIRM, {
				color: 'danger',
				title: 'Remove Property',
				children: (
					<div>You need to contact Support to delete this property</div>
				),
				noConfirmButton: true,
				closeLabel: 'Close',
			}),
		);
	};

	const handleViewUser = (user) => {
		dispatch(
			openModal(MODAL_TYPES.PORTFOLIO_USER_VIEW_MODAL, {
				user,
				portfolioSetId: portfolioData.id,
				portfolioName: portfolioData.name,
				users: portfolioUsers,
				hasAdminAccess: portfolio?.isOwned || portfolio?.role === 'admin',
				properties: portfolio?.properties || [],
				isOwner: portfolio?.isOwned,
			}),
		);
	};

	// Only the current portfolio's page shows: a page still loading for another one never does
	const visibleProperties = useMemo(() => {
		if (loadedPortfolioId !== portfolioId || !Array.isArray(portfolioProperties)) {
			return [];
		}
		return portfolioProperties.map((item) => ({
			...item,
			id: item.id || item.property_id,
		}));
	}, [portfolioProperties, loadedPortfolioId, portfolioId]);

	// An empty result comes back without a paginator, so it counts as nothing found
	const totalPropertiesCount = visibleProperties.length > 0 ? (portfolioPropertiesPagination?.total ?? visibleProperties.length) : 0;
	const propertiesCurrentPage = portfolioPropertiesPagination?.currentPage || propertyListQuery.page;
	const propertiesPerPage = portfolioPropertiesPagination?.perPage || PORTFOLIO_PROPERTIES_PER_PAGE;
	const isLoadingProperties = isLoadingPortfolioProperties || loadedPortfolioId !== portfolioId;

	const handleRevokeInvitation = (invitation) => {
		// OrganizationProvider resolves activeOrganization on login and this
		// button is disabled below until it does, but the guard stays here
		// too: if this branch is ever reached anyway, it must surface a
		// real, user-facing error rather than a silent no-op.
		if (!activeOrganization?.id) {
			console.error(
				'Cannot revoke invitation: active organization is unknown',
				{ invitationId: invitation.id },
			);
			dispatch(
				showToast({
					title: 'Error',
					body: 'Unable to revoke invitation: your organization has not loaded yet. Please try again in a moment.',
					type: 'error',
				}),
			);
			return;
		}

		dispatch(
			openModal(MODAL_TYPES.CONFIRM, {
				color: 'danger',
				title: 'Revoke Invitation',
				onConfirm: async () => {
					try {
						await dispatch(
							revokeInvitation(activeOrganization.id, invitation.id),
						);
						// Refresh portfolio data so the revoked invitation disappears
						dispatch(fetchPortfolioById(portfolioData.id));
					} catch (error) {
						console.error('Failed to revoke invitation', {
							invitationId: invitation.id,
							error,
						});
						dispatch(
							showToast({
								title: 'Error',
								body: 'Failed to revoke invitation. Please try again.',
								type: 'error',
							}),
						);
					}
				},
				children: (
					<div>
						Are you sure you want to revoke the invitation sent to &quot;
						{invitation.email}&quot;? This action cannot be undone.
					</div>
				),
				confirmLabel: 'Revoke Invitation',
			}),
		);
	};

	return (
		<DashboardLayout>
			<div className="portfolio-detail-wrapper violerts-v3-root">
				{/* Top Bar: Back Button + Portfolio Title + Users Avatar Group + Add Users Button */}
				<div className="portfolio-detail-topbar">
					<div className="portfolio-topbar-left">
						<button
							type="button"
							className="portfolio-back-btn"
							onClick={handleBackToPortfolios}
							style={{
								backgroundColor: '#F7F7F7',
								border: '1px solid #C4C4C4',
								borderRadius: '4px',
								height: '32px',
								padding: '0 16px',
								fontSize: '13px',
								fontWeight: 500,
								color: '#161616',
								display: 'inline-flex',
								alignItems: 'center',
								justifyContent: 'center',
								gap: '6px',
								cursor: 'pointer',
								boxSizing: 'border-box',
							}}
						>
							<i className="bi bi-arrow-left" />
							<span>Back</span>
						</button>
						{isLoadingPortfolio ? (
							<h1 className="portfolio-detail-title">Loading...</h1>
						) : portfolioError ? (
							<h1 className="portfolio-detail-title text-danger">Error Loading Portfolio</h1>
						) : (
							<h1 className="portfolio-detail-title">{portfolioData.name}</h1>
						)}
					</div>

					<div className="portfolio-topbar-right">
						<div className="portfolio-topbar-avatars">
							<UserAvatarGroup
								referenceId={`detail-${portfolioData.id}`}
								users={portfolioUsers}
								maxVisible={5}
								size="medium"
								showTooltip={true}
								onUserClick={(user) => handleViewUser(user)}
								onMoreClick={() => {
									if (portfolioUsers && portfolioUsers.length > 0) {
										handleViewUser(portfolioUsers[0]);
									}
								}}
							/>
						</div>
						<PortfolioPermission portfolio={portfolio} requiredPermission="admin">
							<button
								type="button"
								className="portfolio-add-users-btn"
								onClick={handleAddUser}
								style={{
									backgroundColor: '#F7F7F7',
									border: '1px solid #C4C4C4',
									borderRadius: '4px',
									height: '32px',
									padding: '0 16px',
									fontSize: '13px',
									fontWeight: 500,
									color: '#161616',
									display: 'inline-flex',
									alignItems: 'center',
									justifyContent: 'center',
									gap: '6px',
									marginLeft: '16px',
									cursor: 'pointer',
									boxSizing: 'border-box',
									whiteSpace: 'nowrap',
								}}
							>
								Add Users +
							</button>
						</PortfolioPermission>
					</div>
				</div>

				{/* Pending invitations -- not users yet, kept as a separate list and
					rendered as visually distinct cards. The API only returns these for
					an owner or admin, so this section renders nothing for a viewer. */}
				{pendingInvitations.length > 0 && (
					<div className="portfolio-pending-invitations tw-mb-4">
						<h2 className="portfolio-section-title tw-mb-2 tw-text-sm tw-font-semibold">
							Pending Invitations
						</h2>
						<div className="tw-flex tw-flex-wrap tw-gap-3">
							{pendingInvitations.map((invitation) => (
								<PendingInvitationCard
									key={`invitation-${invitation.id}`}
									invitation={invitation}
									onRevoke={handleRevokeInvitation}
									revokeDisabled={!activeOrganization?.id}
								/>
							))}
						</div>
					</div>
				)}

				{/* 3 Stat Cards Row: Properties Saved, Open Violations, Open Certificates */}
				<div className="portfolio-stat-grid">
					{/* Stat Card 1: Properties Saved */}
					<div className="portfolio-stat-box">
						<div className="portfolio-stat-header">
							<i className="bi bi-building portfolio-stat-icon" />
							<span className="portfolio-stat-label">Properties Saved</span>
						</div>
						<div className="portfolio-stat-value">
							{portfolioData.propertyCount ?? 0}
						</div>
					</div>

					{/* Stat Card 2: Open Violations */}
					<div className="portfolio-stat-box">
						<div className="portfolio-stat-header">
							<i className="bi bi-exclamation-circle portfolio-stat-icon" />
							<span className="portfolio-stat-label">Open Violations</span>
						</div>
						<div className="portfolio-stat-value">
							{portfolioData.openViolationsCount !== undefined && portfolioData.openViolationsCount !== null
								? portfolioData.openViolationsCount
								: '--'}
						</div>
					</div>

					{/* Stat Card 3: Open Certificates */}
					<div className="portfolio-stat-box">
						<div className="portfolio-stat-header">
							<i className="bi bi-file-earmark-text portfolio-stat-icon" />
							<span className="portfolio-stat-label">Open Certificates</span>
						</div>
						<div className="portfolio-stat-value">
							{portfolioData.certificatesCount !== undefined && portfolioData.certificatesCount !== null
								? portfolioData.certificatesCount
								: '--'}
						</div>
					</div>
				</div>

				{/* Properties Section Card */}
				<div className="portfolio-detail-card mb-4">
					<div className="portfolio-detail-card-header">
						<div className="portfolio-header-left">
							<h2 className="portfolio-detail-card-title">Properties</h2>
						</div>

						<div className="portfolio-header-center">
							<div className="portfolio-search-box">
								<SearchIcon className="portfolio-search-icon" aria-hidden="true" />
								<input
									type="text"
									placeholder="Search"
									value={propertySearchQuery}
									onChange={(e) => setPropertySearchQuery(e.target.value)}
									className="portfolio-search-input"
									style={{
										border: '1px solid #C4C4C4',
										backgroundColor: '#FFFFFF',
										borderRadius: '4px',
									}}
								/>
							</div>
						</div>

						<div className="portfolio-header-right">
							<PortfolioPermission portfolio={portfolio} requiredPermission="admin">
								<button
									type="button"
									className="portfolio-create-btn"
									onClick={handleAddProperty}
								>
									Add Property +
								</button>
							</PortfolioPermission>
						</div>
					</div>

					{/* Properties Table */}
					<div className="portfolio-detail-table-wrapper table-responsive">
						<table className="table portfolio-detail-table mb-0">
							<thead>
								<tr>
									<th scope="col" className="property-address-col">Property Address</th>
									<th scope="col" className="bin-col">BIN</th>
									<th scope="col" className="block-col">Block</th>
									<th scope="col" className="lot-col">Lot</th>
									<th scope="col" className="settings-col text-center">Settings</th>
								</tr>
							</thead>
							<tbody>
								{(!visibleProperties || visibleProperties.length === 0) ? (
									<tr>
										<td colSpan={5} className="text-center py-4 text-muted">
												{isLoadingProperties
													? 'Loading properties...'
													: portfolioPropertiesError
													? 'Properties could not be loaded. Please try again.'
													: propertySearchQuery
													? `No properties match "${propertySearchQuery}"`
													: isViewer
													? 'No properties assigned. Please contact the Portfolio Admin.'
													: 'No properties found in this portfolio.'}
										</td>
									</tr>
								) : (
									visibleProperties.map((property) => (
										<tr key={property.id || property.property_id} className="portfolio-table-row">
											{/* Property Address + Thumbnail */}
											<td className="property-address-cell">
												<div className="property-item-with-thumb">
													{property.thumbnail_url ? (
														<img
															className="property-thumb"
															src={`${API_URL}${property.thumbnail_url}`}
															alt={property.address || 'Property'}
															onError={(e) => {
																e.target.style.display = 'none';
															}}
														/>
													) : (
														<div className="property-thumb-placeholder">
															<i className="bi bi-building" />
														</div>
													)}
													<div className="property-name-text">
														{property.address ? (
															<Link
																to={`/property/${property.address}/overview`}
																className="property-link"
															>
																{property.address}
															</Link>
														) : (
															'-'
														)}
													</div>
												</div>
											</td>

											{/* BIN */}
											<td className="bin-cell">{property.bin || '-'}</td>

											{/* Block */}
											<td className="block-cell">{property.block || '-'}</td>

											{/* Lot */}
											<td className="lot-cell">{property.lot || '-'}</td>

											{/* Settings / Actions */}
											<td className="settings-cell text-center">
												<div className="property-settings-actions">
													<button
														type="button"
														className="property-action-icon-btn property-notify-icon-btn"
														title="Notifications"
														onClick={() =>
															dispatch(
																openModal(
																	MODAL_TYPES.PROPERTY_NOTIFICATION_MODAL,
																	{
																		portfolioSetId: portfolioData?.id,
																		property,
																	},
																),
															)
														}
													>
														<i className="bi bi-bell" />
													</button>
													<PortfolioPermission
														portfolio={portfolio}
														requiredPermission="admin"
													>
														<button
															type="button"
															className="property-action-icon-btn property-trash-icon-btn"
															title="Remove property"
															onClick={() => handleDeleteProperty(property)}
														>
															<i className="bi bi-trash" />
														</button>
													</PortfolioPermission>
												</div>
											</td>
										</tr>
									))
								)}
							</tbody>
						</table>

						{/* Properties Pagination */}
						{Boolean(totalPropertiesCount > 0 && Math.ceil(totalPropertiesCount / (propertiesPerPage || 10)) > 1) && (
							<div className="portfolio-pagination-footer">
								<div className="portfolio-pagination-info">
									Showing {Math.min(((propertiesCurrentPage || 1) - 1) * (propertiesPerPage || 10) + 1, totalPropertiesCount)} to {Math.min((propertiesCurrentPage || 1) * (propertiesPerPage || 10), totalPropertiesCount)} of {totalPropertiesCount} properties
								</div>
								<PaginatorV3
									currentPage={propertiesCurrentPage || 1}
									totalPages={Math.ceil(totalPropertiesCount / (propertiesPerPage || 10))}
									onPageChange={handlePageChange}
								/>
							</div>
						)}
					</div>
				</div>

				{/* Recent Alerts Section */}
				<RecentAlertsSection portfolioId={portfolioId} />



				{/* Messages Section (hidden for now, see SHOW_PORTFOLIO_MESSAGES) */}
				{SHOW_PORTFOLIO_MESSAGES && portfolio && portfolio.id ? (
					<PortfolioMessages
						portfolioId={portfolio.id}
						portfolioUsers={portfolioUsers}
					/>
				) : null}
			</div>
		</DashboardLayout>
	);
};

export default PortfolioDetailPage;
